import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { auditAskQuery, type AskArea } from '../../api/auditAnswers'
import { ApiError } from '../../api/client'
import { AdmPageHead } from '../../softsee/adm/AdmPageHead'
import { Forbidden } from '../../ui/Forbidden'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'
import { usePageStyles } from '../../ui/usePageStyles'
import { AskQuestionRow } from './AskQuestionRow'

/**
 * `settings/Audit::ask` - ce ne poate spune numai clientul, la
 * /app/settings/audit/ask/$type. Macheta: `settings/audit/ask.php`.
 *
 * Singurul ecran din zona pe care il vede cineva care NU e auditor. De aia nu
 * seamana cu celelalte: fara coduri de arie, fara pastile colorate. O
 * intrebare, cinci propozitii, si un rand liber pentru ce n-am stiut sa
 * intrebam. Se salveaza la fiecare alegere, nu la un buton de la sfarsit.
 */
const STYLES = ['css/adm.css', 'css/audit.css']

export function AuditAskPage({ type }: { type: number }) {
  const page = useQuery(auditAskQuery(type))
  usePageStyles(STYLES, page.data?.version ?? '')

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error instanceof ApiError && page.error.status === 404) {
    return (
      <Content>
        <div id="content" className="od-audit">
          <p>No such audit type. <a href="/app/settings/audit">Back to IT Audit</a>.</p>
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />

  const { areas, ready, link } = page.data
  let askedAll = 0
  let doneAll = 0
  for (const one of areas) {
    askedAll += one.coverage.asked
    doneAll += one.coverage.answered
  }

  return (
    <Content>
      <div id="content" className="od-audit od-audit-ask">
        <AdmPageHead
          tone="blue-mauve"
          icon="fa-comments"
          title="What only you can tell us"
          note={`${page.data.type.title} — the rest we measure ourselves. Nothing here is a test, and there is no wrong answer: an honest "we do not know" is worth more to the audit than a hopeful yes.`}
          stats={[
            { value: `${doneAll}/${askedAll}`, label: 'answered', tone: 'blue-mauve' },
            { value: String(areas.length), label: 'subjects', tone: 'blue-mauve' },
          ]}
        />

        <AskLink link={link} />

        {!ready && (
          <div className="adm-card">
            <strong>The answers table is not on this instance yet.</strong>{' '}
            <span className="adm-muted">
              Change Log &rsaquo; Re-apply schema creates <code>it_audit_answers</code>.
            </span>
          </div>
        )}

        {areas.map((one) => (
          <AskAreaCard key={one.area.code} one={one} />
        ))}
      </div>
    </Content>
  )
}

/**
 * Legatura de trimis omului care raspunde. NU un jeton public: un cap care
 * SCRIE fara autentificare, pe portalul unei banci, e fix ce pica la un audit.
 */
function AskLink({ link }: { link: string }) {
  const [copied, setCopied] = useState(false)
  const full = `${window.location.origin}${link}`

  return (
    <div className="adm-card od-ask-link">
      <span className="od-audit-run-label">Send this to whoever answers</span>
      <input type="text" className="form-control input-sm" id="askLink" readOnly value={full} onFocus={(e) => e.currentTarget.select()} />
      <button
        type="button"
        className="btn btn-default btn-sm"
        id="askCopy"
        onClick={() => {
          void navigator.clipboard?.writeText(full).then(() => setCopied(true))
        }}
      >
        <i className="far fa-copy" aria-hidden="true" /> {copied ? 'Copied' : 'Copy'}
      </button>
      <span className="adm-muted">
        They sign in with their own account and land here. Answers are saved as they are given, so it can be done in several sittings.
      </span>
    </div>
  )
}

function AskAreaCard({ one }: { one: AskArea }) {
  const [done, setDone] = useState(one.coverage.answered)

  return (
    <section className="adm-card od-ask-area" data-area={one.area.code}>
      <header className="od-ask-head">
        <h4>{one.area.title}</h4>
        <span className="od-ask-count">
          <span className="od-ask-done">{done}</span> of {one.coverage.asked} answered
        </span>
      </header>
      {one.questions.map((q) => (
        <AskQuestionRow key={q.key} area={one.area.code} q={q} have={one.have[q.key] ?? null} onCoverage={setDone} />
      ))}
    </section>
  )
}
